/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { Dictionary, isEmpty } from 'lodash';
import { css } from 'aphrodite';
import { Text } from '@blend/enterprise-components';
import { RegistrationFormOverrides } from '../';
import { BlendBridge, BridgePermissions } from 'blb-types/blendBridge';
import { cardWrapperStyles } from 'blb-components/AuthenticatedApp/DashboardPage/SectionCard';
import { DocumentsCard } from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard';
import { Route } from 'blb-components/AuthenticatedApp/routeConfiguration';
import {
  DocumentTitles,
  EcloseDocumentTypeLabels,
} from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard/constants';
import * as personaDocumentUtils from '../common/utils/documents';
import * as constants from '../common/constants';
import { getSupportContactInfoForPrincipal, renderDefaultDashboardOverview, SupportContact } from '../common';
import {
  renderAdditionalDocuments,
  renderENoteDocumentCard,
  renderReceivedRONClosingDocuments,
} from '../common/utils/renderDocuments';
import { TabType } from 'blb-components/AuthenticatedApp/ProfilePage';
import { hasPermissions } from 'blb-utils/permissions';
import { tabPermissionMap } from '../common/permissions';
import { NotarySidebar } from 'blb-components/AuthenticatedApp/RON/SideBar/NotarySidebar';
import { NotaryProfileHeader } from 'blb-components/AuthenticatedApp/ProfilePage/NotaryProfileHeader';
import { FilterParams, OpsDeskFilterParams } from 'blb-constants/pipelineFilter';
import { Pipeline } from '@blend/search-client';
import { DashboardSectionItem } from 'blb-components/AuthenticatedApp/DashboardPage/DashboardSectionItem';

export { getClosingTypeCopy, getPipelinePollingInterval, getDashboardBanner } from '../common';

export const getSupportContactInfo = (): SupportContact => getSupportContactInfoForPrincipal('notary');
export { shouldDownloadModalInterrupt, shouldShowUnsignedESignUIs, getESignBannerConfig } from '../common/index';

const getHybridDocumentSectionComponents = (
  receivedDocs: BlendBridge.API.Document[],
  docIdToRefMap: Dictionary<EClose.API.DocumentReferenceResponse>,
  additionalClosingDocs: BlendBridge.API.Document[],
  loan: BlendBridge.API.Loan,
  auditEvents: EClose.AuditEventResponse[] | undefined,
): JSX.Element => {
  const dayOfDocs = personaDocumentUtils.filterDocsByDesignations(receivedDocs, docIdToRefMap, ['DAY_OF']);
  const signAheadDocs = personaDocumentUtils.filterDocsByDesignations(receivedDocs, docIdToRefMap, ['SIGN_AHEAD']);
  const eNoteDocs = personaDocumentUtils.filterDocsByDesignations(receivedDocs, docIdToRefMap, ['NOTE']);

  let signAheadDocsTag: JSX.Element | undefined;

  if (loan.areSignAheadDocsSigned !== undefined) {
    signAheadDocsTag = loan.areSignAheadDocsSigned
      ? constants.SIGNATURE_COMPLETED_TAG
      : constants.PENDING_SIGNATURE_TAG;
  }

  return (
    <div className={css(cardWrapperStyles.cardGroup)}>
      {!isEmpty(dayOfDocs) && (
        <div className={css(cardWrapperStyles.singleCardWrapper)}>
          <DocumentsCard
            auditEvents={auditEvents}
            documentTypeLabel={EcloseDocumentTypeLabels.INK_SIGN_CLOSING_PACKAGE}
            documents={dayOfDocs}
            headerChildren={constants.PRINT_FOR_CLOSING_DAY_TAG}
            id="it-LenderInkSign"
            isUploaded={false}
            key="it-LenderInkSign"
            title={DocumentTitles.LENDER_INK_SIGN_CLOSING_DOCUMENTS}
          />
        </div>
      )}
      {!isEmpty(signAheadDocs) && (
        <div className={css(cardWrapperStyles.singleCardWrapper)}>
          <DocumentsCard
            auditEvents={auditEvents}
            documentTypeLabel={EcloseDocumentTypeLabels.ESIGN_CLOSING_PACKAGE}
            documents={signAheadDocs}
            headerChildren={signAheadDocsTag}
            id="it-LenderElectronicClosingDocuments"
            isUploaded={false}
            key="it-LenderElectronicClosingDocuments"
            title={DocumentTitles.LENDER_ESIGN_CLOSING_DOCUMENTS}
          />
        </div>
      )}
      {renderAdditionalDocuments(additionalClosingDocs)}
      {renderENoteDocumentCard(loan.closing?.eNoteData, eNoteDocs)}
    </div>
  );
};
const getRonDocumentSectionComponents = (
  receivedDocs: BlendBridge.API.Document[],
  docIdToRefMap: Dictionary<EClose.API.DocumentReferenceResponse>,
  additionalClosingDocs: BlendBridge.API.Document[],
  loan: BlendBridge.API.Loan,
  auditEvents: EClose.AuditEventResponse[] | undefined,
): React.ReactNode => {
  const ronClosingDocumentsCard = renderReceivedRONClosingDocuments(receivedDocs, docIdToRefMap, loan, auditEvents);
  const eNoteDocs = personaDocumentUtils.filterDocsByDesignations(receivedDocs, docIdToRefMap, ['NOTE']);

  return (
    <div className={css(cardWrapperStyles.cardGroup)}>
      {ronClosingDocumentsCard}

      {renderAdditionalDocuments(additionalClosingDocs)}
      {renderENoteDocumentCard(loan.closing?.eNoteData, eNoteDocs)}
    </div>
  );
};
const getTraditionalAndUndeterminedClosingDocumentSectionComponents = (
  receivedDocs: BlendBridge.API.Document[],
  additionalClosingDocs: BlendBridge.API.Document[],
  auditEvents: EClose.AuditEventResponse[] | undefined,
): React.ReactNode => {
  return (
    <div className={css(cardWrapperStyles.cardGroup)}>
      {!isEmpty(receivedDocs) && (
        <div className={css(cardWrapperStyles.singleCardWrapper)}>
          <DocumentsCard
            auditEvents={auditEvents}
            documentTypeLabel={EcloseDocumentTypeLabels.INK_SIGN_CLOSING_PACKAGE}
            documents={receivedDocs}
            headerChildren={constants.PRINT_FOR_CLOSING_DAY_TAG}
            id="it-LenderInkSign"
            isUploaded={false}
            key="it-LenderInkSign"
            title={DocumentTitles.LENDER_INK_SIGN_CLOSING_DOCUMENTS}
          />
        </div>
      )}
      {renderAdditionalDocuments(additionalClosingDocs)}
    </div>
  );
};

// exported for tests only
export const getDocumentSectionConfigs = (
  loan: BlendBridge.API.Loan,
  documents: BlendBridge.API.Document[],
): DashboardSectionItem.SectionConfig[] => {
  const closingType = loan.closing?.ronSelectionData?.derivedClosingType || loan.closing?.closingType;
  const auditEvents = loan.closing?.auditEvents;
  const allClosingDocumentReferences = personaDocumentUtils.getAllClosingDocumentReferences(
    loan.closing?.documentReferences,
    auditEvents,
  );
  const docIdToRefMap = personaDocumentUtils.convertDocumentReferencesToDocIdToRefMap(allClosingDocumentReferences);

  const allReceivedDocs = personaDocumentUtils.getAllReceivedDocumentsForUsers(new Set(), documents);
  const receivedDocs = personaDocumentUtils.getReceivedClosingDocumentsFromAllReceivedDocs(allReceivedDocs);
  const additionalClosingDocs = personaDocumentUtils.filterDocsByTypes(documents, [
    constants.ECloseDocumentTypes.ADDITIONAL_CLOSING_DOCUMENT,
    constants.ECloseDocumentTypes.TRAILING_DOCUMENTS,
  ]);

  const receivedDocComponent = (() => {
    switch (closingType) {
      case 'HYBRID': {
        return getHybridDocumentSectionComponents(
          receivedDocs,
          docIdToRefMap,
          additionalClosingDocs,
          loan,
          auditEvents,
        );
      }
      case 'RON': {
        return getRonDocumentSectionComponents(receivedDocs, docIdToRefMap, additionalClosingDocs, loan, auditEvents);
      }
      case 'TRADITIONAL':
      default: {
        return getTraditionalAndUndeterminedClosingDocumentSectionComponents(
          receivedDocs,
          additionalClosingDocs,
          auditEvents,
        );
      }
    }
  })();

  return [
    {
      content: isEmpty(receivedDocs) ? <Text>No documents yet.</Text> : receivedDocComponent,
      title: 'Received Documents',
    },
  ];
};

export const renderDashboardOverview = (
  dashboardSections: DashboardSectionItem.SectionConfig[],
  loan: BlendBridge.API.Loan,
  documents: BlendBridge.API.Document[],
): React.ReactElement =>
  renderDefaultDashboardOverview(dashboardSections, loan, getDocumentSectionConfigs(loan, documents));

export const getRegisrationFormOverrides = (): RegistrationFormOverrides => ({});

export const shouldUploadModalInterrupt = (): boolean => false;

export const getProfileTabs = (me: BlendBridge.API.User): TabType[] =>
  ['Profile', 'Credentials'].filter((potentialTab): potentialTab is TabType =>
    hasPermissions('notary', me.permissions, tabPermissionMap[potentialTab as TabType]),
  );

export const renderProfileSidebar = () => {
  return <NotarySidebar />;
};

export const showSpecialInstructions = (): boolean => true;

export const getAllowedRoutes = (me: BlendBridge.API.User): { allowedRoutes: Route[]; defaultLandingPath: Route } => {
  if (!me.isProfileComplete) {
    return {
      allowedRoutes: ['/registration'],
      defaultLandingPath: '/registration',
    };
  }

  let defaultLandingPath: Route = '/profile';
  const allowedRoutes: Route[] = ['/profile'];

  if (me.permissions.includes(BridgePermissions.BridgePipeline)) {
    defaultLandingPath = '/pipeline';
    allowedRoutes.push('/pipeline', '/:loanId/dashboard');
  }

  if (me.permissions.includes(BridgePermissions.BSRAdmin)) {
    defaultLandingPath = '/profile';
    allowedRoutes.push('/notary/:principalId/dashboard', '/notaries');
  }

  return {
    allowedRoutes,
    defaultLandingPath,
  };
};

export const renderProfileHeader = () => {
  return <NotaryProfileHeader />;
};

export const getPipelineFilters = (_filterParams: FilterParams): Pipeline.IFilter[] => {
  return [];
};

export const renderPipelineFilter = (
  _filters: OpsDeskFilterParams,
  _onFilterChanged: (filterParams: FilterParams) => void,
) => undefined;
